import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import { listCheckTemplates } from "../../utility/checkTemplates";
import { CHECK_GROUPING } from "../../utility/checkPrizeRows";
import { downloadCheckPdf } from "../../utility/generateChecks";
import { supabase } from "../../supabaseClient";

const todayStamp = () => new Date().toISOString().slice(0, 10);

const ExportChecksDialog = ({
  isOpen,
  onClose,
  tournamentId,
  organizationID,
}) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [grouping, setGrouping] = useState(CHECK_GROUPING.perPrize);
  const [checkDate, setCheckDate] = useState(todayStamp());
  const [payoutsLocked, setPayoutsLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [templateRows, verification] = await Promise.all([
          organizationID ? listCheckTemplates(organizationID) : [],
          supabase
            .from("verificationStates")
            .select("isVerified")
            .eq("tournamentId", tournamentId)
            .eq("entityType", "payouts")
            .is("entityId", null)
            .maybeSingle(),
        ]);
        if (cancelled) return;
        setTemplates(templateRows);
        setTemplateId(templateRows[0]?.id || "");
        setPayoutsLocked(Boolean(verification.data?.isVerified));
        setCheckDate(todayStamp());
        setGrouping(CHECK_GROUPING.perPrize);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Could not load check templates.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, organizationID, tournamentId]);

  const goToTemplates = () => {
    onClose();
    navigate("/organization/check-templates");
  };

  const handleGenerate = async () => {
    if (!templateId) {
      setError("Choose a check template.");
      return;
    }
    if (!payoutsLocked) {
      setError("Lock payouts on Finance before printing checks.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      await downloadCheckPdf({
        tournamentId,
        templateId,
        grouping,
        checkDate,
      });
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not generate checks.");
    } finally {
      setGenerating(false);
    }
  };

  const hasTemplate = templates.length > 0;
  const canGenerate = !loading && hasTemplate && payoutsLocked;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Checks"
      subtitle="Print onto 8.5 × 11 check stock at 100% / actual size."
      maxWidth="max-w-lg"
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          {!loading && !hasTemplate ? (
            <PrimaryButton onClick={goToTemplates}>
              Create check template
            </PrimaryButton>
          ) : (
            <PrimaryButton
              onClick={handleGenerate}
              loading={generating}
              disabled={!canGenerate}
            >
              Generate PDF
            </PrimaryButton>
          )}
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-brand-muted">Loading templates…</p>
      ) : !hasTemplate ? (
        <div className="space-y-4">
          {error ? <p className="text-sm text-red-700">{error}</p> : null}
          <p className="text-sm text-brand-muted">
            This organization has no check templates yet. Create one on{" "}
            <Link
              to="/organization/check-templates"
              className="underline"
              onClick={onClose}
            >
              Check templates
            </Link>
            , then come back to Export Checks.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {error ? <p className="text-sm text-red-700">{error}</p> : null}
          {!payoutsLocked ? (
            <p className="text-sm text-brand-muted">
              Lock payouts on Finance before printing checks.
            </p>
          ) : null}
          <label className="block text-sm">
            <span className="block mb-1 font-medium">Check template</span>
            <select
              className="w-full p-2 border border-brand-charcoal/20 rounded-lg"
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.pageLayout === "threePerPage"
                    ? " (3 per page)"
                    : " (voucher)"}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Grouping</legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="check-grouping"
                checked={grouping === CHECK_GROUPING.perPrize}
                onChange={() => setGrouping(CHECK_GROUPING.perPrize)}
              />
              <span>
                One check per prize. Use this when each place should be its own
                check.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="check-grouping"
                checked={grouping === CHECK_GROUPING.perBowler}
                onChange={() => setGrouping(CHECK_GROUPING.perBowler)}
              />
              <span>
                One check per bowler. Combines that bowler’s prizes into one
                payment. Prize lines on the stub list each event.
              </span>
            </label>
          </fieldset>
          <label className="block text-sm">
            <span className="block mb-1 font-medium">Check date</span>
            <input
              type="date"
              className="w-full p-2 border border-brand-charcoal/20 rounded-lg"
              value={checkDate}
              onChange={(event) => setCheckDate(event.target.value)}
            />
          </label>
          <p className="text-xs text-brand-muted">
            In the print dialog, choose actual size or 100%. Turn off fit to
            page.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default ExportChecksDialog;
